import { useEffect, useState } from 'react';
import {
  RetailerArticlesConnectionQueryVariables,
  useRetailerArticlesConnectionQuery,
} from './articlesList.generated';
import { useAppSelector } from '@/store/hooks';
import { ErpType, LocationStatus } from '@/types';
import { ArticleData } from '@agrantis/cart';
import {
  getFinalVolumeDiscounts,
  groupVolumeDiscounts,
} from '@agrantis/cart/volumeDiscounts';

interface ArticlePaginationVariables {
  searchTerm?: string;
  erpCustomerGroupId?: string | null;
  categoryId?: string | null;
  erpCustomerId?: string | null;
}

function compareByrank(a: ArticleData, b: ArticleData) {
  if (a.rank === null && b.rank === null) return 0; // both missing
  if (a.rank === null) return 1; // a missing → b first
  if (b.rank === null) return -1; // b missing → a first
  return b.rank! - a.rank!; // higher rank first
}

const useArticlesPagination = (variables: ArticlePaginationVariables) => {
  const { searchTerm, erpCustomerGroupId, categoryId, erpCustomerId } =
    variables;
  const retailerId = useAppSelector((state) => state.retailer.retailer?.id);
  const [after, setAfter] = useState('1');
  const [cursors, setCursors] = useState<(string | undefined)[]>([]);
  const settings = useAppSelector((state) => state.retailer.retailer?.settings);
  const erpType = settings?.erpType;
  const [accumulatedArticles, setAccumulatedArticles] = useState<ArticleData[]>(
    [],
  );

  const getSearchTerm = () => {
    if (searchTerm) return searchTerm.trim();
    if (categoryId) return '';
    return 'featured_product';
  };

  const queryVariables: RetailerArticlesConnectionQueryVariables = {
    retailerId: retailerId!,
    after,
    first: 6,
    articleSearch: {
      searchTerm: getSearchTerm(),
      taxonomyId: categoryId,
    },
    inventorySearch: {
      locationStatus: [LocationStatus.Active],
      retailerId: retailerId!,
    },
  };

  if (erpCustomerGroupId && erpType === ErpType.Serp) {
    queryVariables.volumeDiscountFilter = {
      erpCustomerGroupId,
    };
  } else if (erpCustomerId) {
    queryVariables.volumeDiscountFilter = {
      erpCustomerId,
    };
  }

  const [{ data, error, fetching }] = useRetailerArticlesConnectionQuery({
    variables: queryVariables,
  });

  const reset = () => {
    setAfter('1');
    setCursors([]);
    setAccumulatedArticles([]);
  };

  useEffect(() => {
    reset();
  }, [searchTerm, categoryId, retailerId]);

  useEffect(() => {
    if (data?.articlesConnection?.edges) {
      const prices = data.currentUser?.prices;

      const processedArticles = data.articlesConnection.edges
        .filter((edge) => edge !== null && edge !== undefined)
        .map((edge) => edge.node)
        .filter((node) => node !== null && node !== undefined)
        .map((article) => {
          const customerPrice = prices?.find(
            (price) => price.articleID === article.id,
          );

          const groupedVolumeDiscounts = groupVolumeDiscounts(
            article.item[0].volumeDiscounts,
          );

          const selectedVolumeDiscount = getFinalVolumeDiscounts(
            groupedVolumeDiscounts,
          );

          const locations = article.item[0].locations.map((location) => ({
            locationID: location.id,
            location: {
              id: location.id,
              name: location.name,
            },
          }));

          return {
            ...article,
            item: {
              ...article.item[0],
              inventoryLocations: locations,
            },
            volumeDiscounts: selectedVolumeDiscount ?? [],
            customerPrice,
            locations,
          };
        });

      if (processedArticles.length > 0) {
        setAccumulatedArticles((prevState) => {
          const seen = new Set(prevState.map((a) => a.id));
          const newOnes = processedArticles.filter((a) => !seen.has(a.id));
          const newOnesSorted = newOnes.sort(compareByrank);
          return [...prevState, ...newOnesSorted];
        });
      }
    }
  }, [data]);

  const hasNextPage = data?.articlesConnection?.edges?.some(
    (edge) => edge.nextCursor !== null,
  );
  const hasPreviousPage = cursors.length > 0;

  const endCursor = data?.articlesConnection?.pageInfo?.endCursor;

  const loadMore = () => {
    // if (!hasNextPage) return;
    const newCursors = [
      ...cursors,
      data?.articlesConnection.pageInfo.endCursor,
    ];
    setCursors(newCursors || []);
    setAfter(endCursor || '0');
  };

  const loadPrevious = () => {
    if (!hasPreviousPage) return;
    const newCursors = cursors.slice(0, -1);
    setCursors(newCursors);
    const prevCursor = newCursors[newCursors.length - 1] || '0';
    setAfter(prevCursor);
  };

  const total = data?.articlesConnection?.pageInfo?.total || 0;

  return {
    data,
    articles: accumulatedArticles,
    fetching,
    error,
    loadMore,
    loadPrevious,
    hasNextPage,
    hasPreviousPage,
    cursors,
    total,
  };
};

export default useArticlesPagination;
